import { Fragment } from 'react';
import { Field, RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Theme, useTheme } from '@mui/material/styles';
import { ComponentProps } from 'lib/component-props';
import {
  SITE_MASTER_COLORS,
  StSiteMasterChip,
  StSiteMasterChipList,
  StSiteMasterSection,
  StSiteMasterStack,
} from 'components/SiteMaster/SiteMaster.styles';
import {
  getFieldValue,
  getMultilistDescriptionFields,
  getMultilistDisplayFields,
  getMultilistDisplayNames,
  useSiteMasterRouteFields,
} from 'components/SiteMaster/utils';
import UiText from 'components/shared/Typography/UiText';

type AboutSiteLocationProps = ComponentProps & {
  fields: {
    sectionTitle?: Field<string>;
    subSectionTitle?: Field<string>;
    conditionsLabel?: Field<string>;
    therapeuticAreaLabel?: Field<string>;
    siteTypeLabel?: Field<string>;
    languagesLabel?: Field<string>;
    patientTagsLabel?: Field<string>;
    primaryLabelBgColor?: Field<string>;
    secondaryLabelBgColor?: Field<string>;
  };
};

type ChipVariant = 'neutral' | 'accent' | 'population';

const CHIP_STYLES = {
  neutral: {
    backgroundColor: SITE_MASTER_COLORS.chipNeutralBg,
    color: SITE_MASTER_COLORS.textPrimary,
  },

  accent: { backgroundColor: SITE_MASTER_COLORS.accentSurface, color: SITE_MASTER_COLORS.teal },

  population: {
    backgroundColor: SITE_MASTER_COLORS.chipPopulationBg,
    color: SITE_MASTER_COLORS.textSecondary,
  },
} as const;

const getSectionHeadingSx = (theme: Theme) => ({
  fontWeight: 700,
  color: theme.palette.themeColor.main,
  fontFamily: `${theme.palette.primaryFontSemiBoldType}, 'Noto Serif', serif`,
  fontSize: 12,
  [theme.breakpoints.down('lg')]: {
    fontSize: '12px',
  },
});

const labelValueTableSx = {
  width: '100%',

  '& .MuiTableCell-root': { border: 0, py: 0.5, px: 0 },
} as const;

const renderLabelValueRow = (
  label: Field<string> | undefined,
  value: string,
  valueContent: React.ReactNode
): JSX.Element | null => {
  if (!value) {
    return null;
  }

  return (
    <TableRow>
      <TableCell sx={{ width: 120, minWidth: 80, verticalAlign: 'top', pr: 2, lineHeight: '100%' }}>
        <UiText bold size="caption" color="secondary">
          <Text field={label} />
        </UiText>
      </TableCell>

      <TableCell
        sx={{
          color: SITE_MASTER_COLORS.textPrimary,
          alignContent: 'center',
        }}
      >
        <Typography
          sx={{
            color: SITE_MASTER_COLORS.textPrimary,
            fontSize: 14,
            lineHeight: '100%',
          }}
        >
          {valueContent}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const renderLabelChips = (
  label: Field<string> | undefined,

  items: Field<string>[],

  variant: ChipVariant,

  theme: Theme,

  backgroundColor?: string
): JSX.Element | null => {
  if (!items.length) {
    return null;
  }

  const chipSx = {
    ...CHIP_STYLES[variant],
    ...(backgroundColor ? { backgroundColor } : {}),
  };

  return (
    <StSiteMasterStack sx={{ gap: 2 }}>
      <Typography
        variant="h3"
        sx={{
          color: SITE_MASTER_COLORS.textPrimary,
          lineHeight: '100%',
          fontSize: 14,
          [theme.breakpoints.down('lg')]: {
            fontSize: 14,
          },
        }}
      >
        <Text field={label} />
      </Typography>

      <StSiteMasterChipList>
        {items.map((item, index) => (
          <StSiteMasterChip key={`${getFieldValue(item)}-${index}`} sx={chipSx}>
            <Text field={item} />
          </StSiteMasterChip>
        ))}
      </StSiteMasterChipList>
    </StSiteMasterStack>
  );
};

const AboutSiteLocation = (props: AboutSiteLocationProps): JSX.Element => {
  const theme = useTheme();
  const routeFields = useSiteMasterRouteFields();
  const { fields } = props;
  const conditions = getMultilistDescriptionFields(routeFields.siteAssociatedConditions);
  const communities = getMultilistDescriptionFields(routeFields.siteAssociatedCommunities);
  const recruitingTypes = getMultilistDisplayFields(routeFields.siteRecruitingType);
  const siteType = getFieldValue(routeFields.siteType);
  const languageFields = getMultilistDisplayFields(routeFields.siteSupportedLanguages);
  const languages = getMultilistDisplayNames(routeFields.siteSupportedLanguages).join(', ');

  const hasSpecialtiesContent =
    conditions.length > 0 || communities.length > 0 || recruitingTypes.length > 0;
  const hasSiteDetails = siteType || languages || routeFields.siteDescription?.value;

  if (!hasSiteDetails && !hasSpecialtiesContent) {
    return <></>;
  }

  return (
    <StSiteMasterSection
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
      id="about-location-section"
    >
      {hasSiteDetails && (
        <StSiteMasterStack sx={{ gap: 3 }}>
          {fields.sectionTitle && (
            <Typography
              variant="h2"
              id="about-site-location-heading"
              sx={getSectionHeadingSx(theme)}
            >
              <Text field={fields.sectionTitle} />
            </Typography>
          )}
          {(siteType || languages) && (
            <Table sx={labelValueTableSx}>
              <TableBody>
                {renderLabelValueRow(
                  fields.siteTypeLabel,
                  siteType,
                  <Text field={routeFields.siteType} />
                )}
                {renderLabelValueRow(
                  fields.languagesLabel,
                  languages,
                  languageFields.map((languageField, index) => (
                    <Fragment key={`${getFieldValue(languageField)}-${index}`}>
                      {index > 0 && ', '}
                      <Text field={languageField} />
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          {routeFields.siteDescription && (
            <UiText
              size="caption"
              color="black"
              styles={{ lineHeight: '160%', fontSize: 16, color: SITE_MASTER_COLORS.textSecondary }}
              id="site-description"
            >
              <RichText field={routeFields.siteDescription} />
            </UiText>
          )}
        </StSiteMasterStack>
      )}

      {hasSpecialtiesContent && (
        <StSiteMasterStack sx={{ gap: 3 }}>
          <Typography variant="h3" sx={getSectionHeadingSx(theme)}>
            <Text field={fields.subSectionTitle} />
          </Typography>

          {renderLabelChips(
            fields.therapeuticAreaLabel,
            communities,
            'accent',
            theme,
            getFieldValue(fields.primaryLabelBgColor) || undefined
          )}

          {renderLabelChips(
            fields.conditionsLabel,
            conditions,
            'neutral',
            theme,
            getFieldValue(fields.secondaryLabelBgColor) || undefined
          )}

          {renderLabelChips(fields.patientTagsLabel, recruitingTypes, 'population', theme)}
        </StSiteMasterStack>
      )}
    </StSiteMasterSection>
  );
};

export default AboutSiteLocation;
